import React, { useState, useEffect, useRef, useCallback } from 'react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../../components/ui/dialog';
import { Alert, AlertDescription } from '../../components/ui/alert';
import { Progress } from '../../components/ui/progress';
import { AlertCircle, Clock } from 'lucide-react';
import { Product, getProductId, getProductTitle } from '../../types/product';
import { Prompt, PromptStatus } from '../../types/prompt';
import { Button } from '../../components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { optimizationService } from '../../services/optimization/OptimizationService';
interface OptimizeDialogProps {
  isOpen: boolean;
  onClose: () => void;
  product: Product;
  marketplace: 'Shopify' | 'Google Shopping' | 'Amazon'; // These must match the case in initializeFirestoreAdmin.cjs
  onOptimizeComplete?: () => void;
}
const OptimizeDialog: React.FC<OptimizeDialogProps> = ({
  isOpen,
  onClose,
  product,
  marketplace,
  onOptimizeComplete
}) => {
  // Witty messages that rotate during optimization
  const wittyMessages = React.useMemo(() => [
    "Teaching AI to write product descriptions... it's like poetry, but with more keywords 🎨",
    "Brewing the perfect blend of SEO and readability ☕",
    "Making your product shine brighter than a diamond in a coal mine ✨",
    "Sprinkling some marketing magic dust... *sparkles* ✨",
    "Converting tech specs into human speak... beep boop 🤖",
    "Crafting descriptions so good, they'll make your competitors jealous 😎",
    "Adding a pinch of SEO, a dash of style, and a whole lot of awesome 🌟",
    "Making your product look better than your competitor's... legally 😉",
    "Optimizing keywords faster than you can say 'e-commerce' 🚀",
    "Teaching robots to write like Shakespeare... but for products 🎭",
    "Turning product features into customer benefits... like magic! 🎩",
    "Making your listing so good, even your mom would buy it 💝",
    "Calculating the perfect word-to-emoji ratio... 🤔",
    "Translating 'meh' into 'must-have'... 📈",
    "Polishing your listing until it sparkles... ✨",
    "Making your product description pop like bubble wrap 💫",
    "Crafting copy that would make Don Draper proud 🎯",
    "Adding just the right amount of pizzazz... 🎪",
    "Optimizing optimization... optimistically 🎈",
    "Making your listing stand out like a unicorn at a horse show 🦄",
    "Turning features into benefits faster than a caffeinated copywriter ☕",
    "Adding SEO sprinkles to your product sundae 🍨",
    "Making your listing more magnetic than a superconductor 🧲",
    "Crafting descriptions that would make Hemingway proud 📚",
    "Teaching AI the fine art of product storytelling 🎨",
    "Optimizing your listing with digital fairy dust ✨",
    "Making your product page prettier than a peacock 🦚",
    "Brewing up some marketing magic in our digital cauldron 🪄",
    "Turning your product specs into customer dreams 💫",
    "Adding that special sauce to your listing 🌟"
  ], []);

  // Refs for intervals, timeouts and state
  const messageIntervalRef = useRef<NodeJS.Timeout>();
  const messageTimeoutRef = useRef<NodeJS.Timeout>();
  const progressIntervalRef = useRef<NodeJS.Timeout>();
  const closeTimeoutRef = useRef<NodeJS.Timeout>();
  const hasStartedRef = useRef(false);
  const recentMessagesRef = useRef<string[]>([]);

  // State
  const [optimizationMessage, setOptimizationMessage] = useState('');
  const [isOptimizing, setIsOptimizing] = useState(false);
  // Shuffle array using Fisher-Yates algorithm
  const shuffleArray = useCallback((array: string[]) => {
    const shuffled = [...array];
    for (let i = shuffled.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1));
      [shuffled[i], shuffled[j]] = [shuffled[j], shuffled[i]];
    }
    return shuffled;
  }, []);

  // Function to get a truly random message
  const getRandomMessage = useCallback((currentMessage: string) => {
    // If we haven't used any messages yet, shuffle the array
    if (recentMessagesRef.current.length === 0) {
      const shuffled = shuffleArray(wittyMessages);
      recentMessagesRef.current = [shuffled[0]];
      return shuffled[0];
    }

    // Filter out recently used messages
    const availableMessages = wittyMessages.filter(msg => 
      !recentMessagesRef.current.includes(msg)
    );

    // If we've used all messages, shuffle and start over
    if (availableMessages.length === 0) {
      const shuffled = shuffleArray(wittyMessages.filter(msg => msg !== currentMessage));
      recentMessagesRef.current = [shuffled[0]];
      return shuffled[0];
    }

    // Pick a random message from available ones
    const randomIndex = Math.floor(Math.random() * availableMessages.length);
    const newMessage = availableMessages[randomIndex];
    
    // Update recent messages history (keep last 5)
    recentMessagesRef.current = [newMessage, ...recentMessagesRef.current].slice(0, 5);
    return newMessage;
  }, [wittyMessages, shuffleArray]);

  const [progress, setProgress] = useState(0);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [prompts, setPrompts] = useState<Prompt[]>([]);
  const [selectedPrompt, setSelectedPrompt] = useState('');
  const [isLoadingPrompts, setIsLoadingPrompts] = useState(false);

    // Function to clean up all intervals, timeouts and reset state
    const clearAllTimers = useCallback(() => {
      try {
        // Clear all intervals and timeouts
        if (progressIntervalRef.current) {
          clearInterval(progressIntervalRef.current);
          progressIntervalRef.current = undefined;
        }
        if (messageIntervalRef.current) {
          clearInterval(messageIntervalRef.current);
          messageIntervalRef.current = undefined;
        }
        if (messageTimeoutRef.current) {
          clearTimeout(messageTimeoutRef.current);
          messageTimeoutRef.current = undefined;
        }
        if (closeTimeoutRef.current) {
          clearTimeout(closeTimeoutRef.current);
          closeTimeoutRef.current = undefined;
        }

        // Reset all state
        recentMessagesRef.current = [];
        setOptimizationMessage('');
        setProgress(0);
        setError('');
        setSuccess('');
        hasStartedRef.current = false;
        setIsOptimizing(false);
        // Don't reset selectedPrompt here as it's needed for the prompt preview
        // Reset selectedPrompt only when dialog closes
      } catch (error) {
        console.error('Error cleaning up timers:', error);
      }
    }, []);

    // Reset state and cleanup when dialog closes
    const handleClose = useCallback(() => {
      // Clear any existing timeouts first
      if (closeTimeoutRef.current) {
        clearTimeout(closeTimeoutRef.current);
        closeTimeoutRef.current = undefined;
      }
      clearAllTimers();
      hasStartedRef.current = false;
      setSelectedPrompt('');
      onClose();
    }, [clearAllTimers, onClose]);

    // Cleanup on unmount
    useEffect(() => {
      return clearAllTimers;
    }, [clearAllTimers]);

  // Memoize loadPrompts function
  const loadPrompts = useCallback(async () => {
    setIsLoadingPrompts(true);
    setError('');
    
    try {
      // Special handling for Google Shopping
      const normalizedMarketplace = marketplace.toLowerCase() === 'google shopping' 
        ? 'Google Shopping'
        : marketplace.charAt(0).toUpperCase() + marketplace.slice(1).toLowerCase();

      // Get the default prompt ID for this marketplace
      const defaultPromptId = `default-${normalizedMarketplace.toLowerCase().replace(/\s+/g, '-')}-prompt`;
      console.log('Looking for default prompt:', defaultPromptId);

      // Pass marketplace directly - OptimizationService handles case normalization
      const marketplacePrompts = await optimizationService.getPrompts(normalizedMarketplace);
      console.log('Received prompts for', normalizedMarketplace, ':', marketplacePrompts);

      if (marketplacePrompts.length === 0) {
        throw new Error(`No prompts found for ${normalizedMarketplace}`);
      }

      // Filter out inactive prompts and ensure status is defined
      const activePrompts = marketplacePrompts
        .filter(prompt => prompt.isDefault || prompt.status === 'Active')
        .map(prompt => ({
          ...prompt,
          status: prompt.isDefault ? 'Active' as PromptStatus : (prompt.status || 'Active') as PromptStatus
        })) as Prompt[];
      
      console.log('Setting prompts:', activePrompts);
      setPrompts(activePrompts);
      
      // Set default prompt if available
      const defaultPrompt = activePrompts.find(p => p.id === defaultPromptId);
      if (defaultPrompt) {
        console.log('Setting default prompt:', defaultPrompt.id);
        setSelectedPrompt(defaultPrompt.id);
      } else {
        console.log('No default prompt found, using first prompt:', marketplacePrompts[0].id);
        setSelectedPrompt(marketplacePrompts[0].id);
      }
    } catch (error) {
      console.error('Error loading prompts:', error);
      setError(error instanceof Error ? error.message : 'Failed to load prompts');
    } finally {
      setIsLoadingPrompts(false);
    }
  }, [marketplace]); // Only depend on marketplace

  useEffect(() => {
    if (!isOpen) {
      clearAllTimers();
      setProgress(0);
      setError('');
      setSuccess('');
      setSelectedPrompt('');
      hasStartedRef.current = false;
      setIsOptimizing(false);
    } else {
      // Load prompts when dialog opens
      loadPrompts();
    }
  }, [isOpen, loadPrompts, clearAllTimers]);

  const startOptimization = useCallback(async () => {
    console.log('startOptimization called with:', {
      isOptimizing,
      selectedPrompt,
      productId: getProductId(product),
      product
    });
    
    if (isOptimizing || !selectedPrompt || !getProductId(product)) {
      console.log('startOptimization guard clause triggered:', {
        isOptimizing,
        hasSelectedPrompt: !!selectedPrompt,
        hasProductId: !!getProductId(product)
      });
      return;
    }
    
    // Clear any existing timers and reset state
    clearAllTimers();

    // Set states in correct order
    hasStartedRef.current = true;
    console.log('hasStartedRef.current set to:', hasStartedRef.current);
    
    // Set states in correct order
    setIsOptimizing(true);
    setProgress(0);
    setError('');
    setSuccess('');

    // Set initial message
    const initialMessage = getRandomMessage('');
    setOptimizationMessage(initialMessage);

    // Start message rotation after 5 seconds
    messageTimeoutRef.current = setTimeout(() => {
      messageIntervalRef.current = setInterval(() => {
        setOptimizationMessage(prevMessage => getRandomMessage(prevMessage));
      }, 5000);
    }, 5000);

    // Start progress animation with easing
    let startTime = Date.now();
    const initialDuration = 20000; // 20 seconds to reach 90%
    const perPercentDuration = 5000; // 5 seconds per percent after 90%
    let lastProgress = 0;

    const updateProgress = () => {
      const elapsed = Date.now() - startTime;
      let currentProgress;

      try {
        if (elapsed < initialDuration) {
          // Normal progression up to 90% with easing
          const progress = elapsed / initialDuration;
          currentProgress = Math.min(progress * progress * 90, 90); // Quadratic easing
        } else {
          // Very slow progression from 90% to 100%
          const extraElapsed = elapsed - initialDuration;
          const extraProgress = Math.floor(extraElapsed / perPercentDuration);
          // Ensure we don't jump backwards
          currentProgress = Math.max(
            lastProgress,
            Math.min(90 + extraProgress, 99.9)
          );
        }

        lastProgress = currentProgress;
        setProgress(currentProgress);
      } catch (error) {
        console.error('Error updating progress:', error);
        // Don't update on error to keep current progress
      }
    };

    // Update progress every 50ms for smooth animation
    progressIntervalRef.current = setInterval(updateProgress, 50);

    try {
      console.log('Starting optimization:', {
        productId: getProductId(product),
        marketplace,
        promptId: selectedPrompt
      });

      await optimizationService.optimizeProduct(getProductId(product), marketplace, selectedPrompt);
      
      // Set success state and cleanup
      setProgress(100);
      setSuccess(`Successfully optimized for ${marketplace}`);
      
      // Call onOptimizeComplete
      if (onOptimizeComplete) {
        await onOptimizeComplete();
      }
      
      // Clear all timers and state
      clearAllTimers();
      hasStartedRef.current = false;
      setIsOptimizing(false);
      
      // Close dialog after a brief moment to show 100%
      closeTimeoutRef.current = setTimeout(() => {
        handleClose();
      }, 500);
    } catch (error) {
      // Clear all timers on error
      clearAllTimers();
      
      // Log the technical error for debugging
      console.error('Optimization error:', error);
      
      // Show user-friendly error message
      setError("Oops!! Our AI robots had a little problem. Please run the optimization again.");
      
      // Reset states
      setProgress(0);
      hasStartedRef.current = false;
      setIsOptimizing(false);
    }
  }, [product, selectedPrompt, marketplace, onOptimizeComplete, getRandomMessage, clearAllTimers, handleClose, isOptimizing]);

  const getSelectedPromptContent = () => {
    const selectedPromptData = prompts.find(p => p.id === selectedPrompt);
    return selectedPromptData?.content || '';
  };

  return (
    <Dialog open={isOpen} onOpenChange={(open) => !open && handleClose()}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle>Optimize Product</DialogTitle>
          <DialogDescription className="text-base">
            {getProductTitle(product)}
          </DialogDescription>
        </DialogHeader>

        {error && (
          <Alert variant="destructive">
            <AlertCircle className="h-4 w-4" />
            <AlertDescription>{error}</AlertDescription>
          </Alert>
        )}

        {!hasStartedRef.current ? (
          // Show prompt selection and preview before optimization starts
          <>
            <div className="space-y-4">
              <div>
                <h3 className="text-sm font-medium mb-2">Select Prompt Template</h3>
                {isLoadingPrompts ? (
                  <div className="text-sm text-gray-500">Loading prompts...</div>
                ) : (
                  <Select
                    value={selectedPrompt}
                    onValueChange={setSelectedPrompt}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Select a prompt template" />
                    </SelectTrigger>
                    <SelectContent>
                      {prompts.map((prompt) => (
                        <SelectItem key={prompt.id} value={prompt.id}>
                          {prompt.title}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                )}
              </div>

              {selectedPrompt && (
                <div>
                  <h3 className="text-sm font-medium mb-2">Prompt Preview</h3>
                  <div className="bg-gray-50 rounded-md p-3 text-sm text-gray-700 max-h-60 overflow-y-auto">
                    {getSelectedPromptContent().split('\n').filter(Boolean).map((line, i) => (
                      <p key={i} className="mb-2">{line}</p>
                    ))}
                  </div>
                </div>
              )}
            </div>

            <DialogFooter>
              <Button variant="outline" onClick={onClose}>
                Cancel
              </Button>
              <Button
                onClick={() => {
                  console.log('Start Optimization button clicked');
                  startOptimization();
                }}
                disabled={!selectedPrompt || isLoadingPrompts}
                className="bg-blue-600 hover:bg-blue-700 text-white"
              >
                Start Optimization
              </Button>
            </DialogFooter>
          </>
        ) : (
          // Show progress and status after optimization starts
          <div className="space-y-6 py-4">
            <Progress value={progress} />
            <div className="text-center space-y-2">
              {progress < 100 ? (
                <>
                  <Clock className="h-6 w-6 mx-auto text-blue-500 animate-pulse" />
                  <p className="text-sm text-gray-600">{optimizationMessage}</p>
                  <p className="text-xs text-gray-500">{Math.round(progress)}% complete</p>
                </>
              ) : success ? (
                <p className="text-green-600">{success}</p>
              ) : null}
            </div>

          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default OptimizeDialog;